/*
    Minimal auth hook: interactive MSAL login + cookie session as source of truth.
*/
'use client';

import { useEffect, useState } from 'react';
import { bootstrapTokenScopes, loginRequest } from '@/config/entra';
import { sessionImpersonationCookieName } from '@/config/session';
import { handleLoginRedirect, initializeMsal, msalInstance, startLoginRedirect } from '@/utils/msalInstance';
import { markPendingAccountSelection } from '../../utils/pendingAccountSelection';
import axios from '@/config/axios';
import {
    getCachedSessionState,
    publishSessionState,
    subscribeSessionState,
} from '@/utils/clientSessionState';

let sessionProbePromise: Promise<boolean> | null = null;

function probeSessionOnce(): Promise<boolean> {
    const cached = getCachedSessionState();
    if (cached !== null) {
        return Promise.resolve(cached);
    }

    if (sessionProbePromise) {
        return sessionProbePromise;
    }

    sessionProbePromise = axios
        .get<{ accountId: string | null }>('/api/sessions', { skipAuth: true })
        .then(res => res.data.accountId !== null)
        .catch(() => false)
        .then(result => {
            publishSessionState(result);
            return result;
        })
        .finally(() => {
            sessionProbePromise = null;
        });

    return sessionProbePromise;
}

export function useEntraAuth() {
    // Reads an in-memory module cache — cheap, and the recomputed value is discarded after mount.
    // react-doctor-disable-next-line react-doctor/rerender-lazy-state-init
    const [hasSession, setHasSession] = useState<boolean | null>(getCachedSessionState());
    const [isAuthenticating, setIsAuthenticating] = useState(false);

    useEffect(() => subscribeSessionState(setHasSession), []);

    // Auth/lookup/file hook that sits outside the React Query data layer by design.
    // react-doctor-disable-next-line no-fetch-in-effect
    useEffect(() => {
        const cached = getCachedSessionState();
        if (cached !== null) {
            setHasSession(cached);
            return;
        }

        void probeSessionOnce();
    }, []);

    const isAuthenticated = hasSession === true;
    const isInitialized = hasSession !== null;

    // Kept inside the auth hook for cohesion — hoisting session logic to module scope would make it callable outside React for a negligible gain.
    // react-doctor-disable-next-line prefer-module-scope-pure-function
    const bootstrapSessionFromToken = async (accessToken: string) => {
        const authHeaders = { Authorization: `Bearer ${accessToken}` };
        const accountsResponse = await axios.get<Array<{ id: string | number; isPrimary?: boolean }>>('/api/accounts', {
            disableToast: true,
            headers: authHeaders,
            skipAuth: true,
        });

        const accounts = Array.isArray(accountsResponse.data) ? accountsResponse.data : [];

        // For multi-account users, pick a provisional account so the session cookie
        // exists (required for subsequent /api/accounts calls in the customer zone),
        // but mark that the user still needs to make an explicit selection.
        if (accounts.length > 1) {
            markPendingAccountSelection();
        }

        const selectedAccount = accounts.find(account => account.isPrimary) ?? accounts[0];
        const selectedAccountId = selectedAccount?.id;

        if (selectedAccountId === undefined || selectedAccountId === null) {
            return false;
        }

        await axios.post(
            '/api/sessions',
            { id: String(selectedAccountId) },
            {
                disableToast: true,
                headers: authHeaders,
                skipAuth: true,
            }
        );

        publishSessionState(true);
        return true;
    };

    const completeRedirectLogin = async (): Promise<{ bootstrapped: boolean; hadRedirectResponse: boolean }> => {
        await initializeMsal();
        let response: Awaited<ReturnType<typeof handleLoginRedirect>>;
        try {
            response = await handleLoginRedirect();
        } catch {
            // MSAL failed to process the redirect (e.g. state mismatch, network error).
            // Treat as a handled redirect so the caller does not restart the login flow.
            return { bootstrapped: false, hadRedirectResponse: true };
        }
        if (!response) {
            return { bootstrapped: false, hadRedirectResponse: false };
        }

        const account = response.account ?? msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
        if (!account) {
            return { bootstrapped: false, hadRedirectResponse: true };
        }

        if (!bootstrapTokenScopes.length) {
            return { bootstrapped: false, hadRedirectResponse: true };
        }

        let bootstrapToken: string | null = null;
        try {
            const tokenResponse = await msalInstance.acquireTokenSilent({
                account,
                scopes: bootstrapTokenScopes,
            });
            bootstrapToken = tokenResponse.accessToken;
        } catch {
            bootstrapToken = null;
        }

        if (!bootstrapToken) {
            return { bootstrapped: false, hadRedirectResponse: true };
        }

        try {
            const bootstrapped = await bootstrapSessionFromToken(bootstrapToken);
            return { bootstrapped, hadRedirectResponse: true };
        } catch {
            return { bootstrapped: false, hadRedirectResponse: true };
        }
    };

    const login = async () => {
        if (isAuthenticated || isAuthenticating) {
            return;
        }

        try {
            setIsAuthenticating(true);
            await initializeMsal();
            return await startLoginRedirect(loginRequest);
        } finally {
            setIsAuthenticating(false);
        }
    };

    type LogoutOptions = {
        postLogoutRedirectPath?: string;
    };

    // Kept inside the auth hook for cohesion — hoisting session logic to module scope would make it callable outside React for a negligible gain.
    // react-doctor-disable-next-line prefer-module-scope-pure-function
    const clearWebsiteSession = async (publishState: boolean = true) => {
        try {
            await axios.delete('/api/sessions', { skipAuth: true });
        } catch {
            // Continue with logout even if session clear request fails.
        }

        if (publishState) {
            publishSessionState(false);
        }
    };

    const logout = async (opts?: LogoutOptions) => {
        const redirectPath = opts?.postLogoutRedirectPath || '/';

        if (isImpersonated) {
            await clearWebsiteSession();
            window.location.assign(redirectPath);
            return;
        }

        await clearWebsiteSession(false);

        try {
            await initializeMsal();
            const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? undefined;
            await msalInstance.logoutRedirect({
                account,
                postLogoutRedirectUri: new URL(redirectPath, window.location.origin).toString(),
            });
            publishSessionState(false);
            window.location.assign(redirectPath);
        } catch {
            publishSessionState(false);
            window.location.assign(redirectPath);
        }
    };

    // TODO: remove this later. Now develop branch depends on this code.
    // Kept inside the auth hook for cohesion — hoisting session logic to module scope would make it callable outside React for a negligible gain.
    // react-doctor-disable-next-line prefer-module-scope-pure-function
    const getUserInfo = () => {
        try {
            const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];

            return account
                ? {
                      email: account.username || '',
                      id: account.localAccountId || '',
                      name: account.name || '',
                      username: account.username || '',
                  }
                : null;
        } catch {
            return null;
        }
    };

    const isImpersonated =
        typeof document !== 'undefined' &&
        document.cookie.split(';').some(c => c.trim().startsWith(`${sessionImpersonationCookieName}=`));

    return {
        completeRedirectLogin,
        getUserInfo,
        hasSession,
        isAuthenticated,
        isAuthenticating,
        isImpersonated,
        isInitialized,
        login,
        logout,
    };
}
